import Box from '@mui/material/Box'
import { useEffect } from 'react'
import type { FC } from 'react'
import MuiModal, { ModalProps } from '@mui/material/Modal'
import Fade from '@mui/material/Fade'
import Close from '@mui/icons-material/Close'

type ModalPropsWithoutChildren = Omit<
  ModalProps,
  'children' | 'onEscapeKeyDown' | 'disableAutoFocus' | 'onClose'
>
interface ChatMessageModalProps extends ModalPropsWithoutChildren {
  closeModal: () => void
  image: string
}

const ChatMessageModal: FC<ChatMessageModalProps> = (props: ChatMessageModalProps) => {
  const { closeModal, image, ...modalProps } = props
  useEffect(() => {
    if (!props.open) {
      return
    }
    const listener = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal()
    }
    window.addEventListener('keydown', listener)
    return () => window.removeEventListener('keydown', listener)
  }, [props.open, closeModal])
  return (
    <MuiModal
      {...modalProps}
      disableAutoFocus
      // v5 removed Modal's onBackdropClick; onClose fires for both backdrop and
      // escape, so narrow to backdrop to preserve the prior behavior (escape is
      // handled by the keydown listener above).
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') closeModal()
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'absolute',
        height: '100%',
        width: '100%',
        cursor: 'pointer',
      }}
    >
      {/* v5's Modal wraps its child in a FocusTrap that holds a ref to the
          child's DOM node, so the child must be a single ref-able element — a
          Fragment can't hold a ref. */}
      <div>
        <Close
          sx={{ position: 'absolute', top: '1rem', right: '1rem', color: 'common.white' }}
          onClick={closeModal}
        />
        <Box sx={{ overflow: 'auto', maxWidth: '92.5%' }}>
          <a href={image} target="blank">
            <Fade in={true}>
              <Box
                component="img"
                sx={{ maxHeight: '100vh', maxWidth: '100%', display: 'block' }}
                src={image}
                alt=""
              />
            </Fade>
          </a>
        </Box>
      </div>
    </MuiModal>
  )
}

export default ChatMessageModal
